import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  Pagination,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { coursesAPI } from '../../services/api';
import SearchInput from '../UIKit/SearchInput';
import { getUserAvatarSrc } from '../../utils/avatarUtils';

const STUDENTS_PER_PAGE = 5;

const StudentModal = ({ open, onClose, courseId }) => {
  const theme = useTheme();
  const { t } = useTranslation(['courses', 'common']);

  // Student states
  const [students, setStudents] = useState([]);
  const [studentsPagination, setStudentsPagination] = useState({
    totalCount: 0,
    currentPage: 1,
    totalPages: 1,
  });
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentsError, setStudentsError] = useState(null);

  // Student statistics states
  const [studentsStats, setStudentsStats] = useState({
    all: 0,
    completed: 0,
    inProgress: 0,
    started: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);

  // Filter states
  const [selectedStudentStatus, setSelectedStudentStatus] = useState('all');
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch student statistics
  const fetchStudentsStats = useCallback(async () => {
    if (!courseId) return;

    setStatsLoading(true);
    setStatsError(null);

    try {
      const response = await coursesAPI.getCourseStudentsStats(courseId);
      const stats = response.data.stats;

      setStudentsStats({
        all: stats.totalEnrolled || 0,
        completed: stats.completed || 0,
        inProgress: stats.inProgress || 0,
        started: stats.started || 0,
      });
    } catch (error) {
      console.error('Error loading student statistics:', error);
      setStatsError(
        error.response?.data?.message ||
          t('courses:courseDetail.errors.statsLoadError')
      );
    } finally {
      setStatsLoading(false);
    }
  }, [courseId, t]);

  // Fetch course students
  const fetchStudents = useCallback(
    async (status = 'all', search = '', page = 1) => {
      if (!courseId) return;

      setStudentsLoading(true);
      setStudentsError(null);

      try {
        const params = {
          page,
          limit: STUDENTS_PER_PAGE,
          ...(status !== 'all' && { status }),
          ...(search && { search }),
        };

        const response = await coursesAPI.getCourseStudents(courseId, params);

        setStudents(response.data.students || []);
        setStudentsPagination(
          response.data.pagination || {
            totalCount: 0,
            currentPage: 1,
            totalPages: 1,
          }
        );
      } catch (error) {
        console.error('Error loading students:', error);
        setStudentsError(
          error.response?.data?.message ||
            t('courses:courseDetail.errors.studentsLoadError')
        );
      } finally {
        setStudentsLoading(false);
      }
    },
    [courseId, t]
  );

  // Load statistics only once when modal opens
  useEffect(() => {
    if (open && courseId) {
      fetchStudentsStats();
    }
  }, [open, courseId, fetchStudentsStats]);

  // Load students when modal opens or filters change
  useEffect(() => {
    if (open && courseId) {
      fetchStudents(selectedStudentStatus, studentSearchTerm, currentPage);
    }
  }, [
    open,
    courseId,
    fetchStudents,
    selectedStudentStatus,
    studentSearchTerm,
    currentPage,
  ]);

  // Filter handlers
  const handleStudentStatusFilter = useCallback(
    (status) => {
      setSelectedStudentStatus(status);
      setCurrentPage(1);
      fetchStudents(status, studentSearchTerm, 1);
    },
    [fetchStudents, studentSearchTerm]
  );

  const handleStudentSearch = useCallback(
    (searchTerm) => {
      setStudentSearchTerm(searchTerm);
      setCurrentPage(1);
      fetchStudents(selectedStudentStatus, searchTerm, 1);
    },
    [fetchStudents, selectedStudentStatus]
  );

  // Pagination handler
  const handlePageChange = useCallback(
    (event, page) => {
      setCurrentPage(page);
      fetchStudents(selectedStudentStatus, studentSearchTerm, page);
    },
    [fetchStudents, selectedStudentStatus, studentSearchTerm]
  );

  // Reset state on close
  const handleClose = useCallback(() => {
    setStudentSearchTerm('');
    setSelectedStudentStatus('all');
    setCurrentPage(1);
    onClose();
  }, [onClose]);

  // Get avatar color based on status
  const getAvatarColor = (status) => {
    switch (status) {
      case 'completed':
        return theme.palette.primary.main;
      case 'in_progress':
        return theme.palette.warning.main;
      case 'started':
        return theme.palette.success.main;
      default:
        return theme.palette.secondary.main;
    }
  };

  // Get background color with opacity for chip
  const getChipBackgroundColor = (status) => {
    const color = getAvatarColor(status);
    // Convert hex to rgba with 0.2 opacity
    const hexToRgba = (hex, alpha) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    return hexToRgba(color, 0.15);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          backgroundColor: theme.palette.background.paper,
          borderRadius: 3,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          maxWidth: '600px',
          width: '100%',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
        }}
      >
        <Typography variant="h6" component="h2">
          {t('courses:courseDetail.courseParticipants', 'Course Participants')}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 3 }}>
        {/* Statistics loading error */}
        {statsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {statsError}
          </Alert>
        )}

        {/* Student statistics */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 1,
            mb: 3,
            textAlign: 'center',
          }}
        >
          <Box
            onClick={() => handleStudentStatusFilter('all')}
            sx={{
              padding: 2,
              borderRadius: 2,
              backgroundColor:
                selectedStudentStatus === 'all'
                  ? theme.palette.action.selected
                  : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {statsLoading ? '...' : studentsStats.all}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              {t('courses:courseDetail.all', 'All')}
            </Typography>
          </Box>
          <Box
            onClick={() => handleStudentStatusFilter('completed')}
            sx={{
              padding: 2,
              borderRadius: 2,
              backgroundColor:
                selectedStudentStatus === 'completed'
                  ? theme.palette.action.selected
                  : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold', mb: 0.5, color: 'primary.main' }}
            >
              {statsLoading ? '...' : studentsStats.completed}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              {t('courses:courseDetail.completed', 'Completed')}
            </Typography>
          </Box>
          <Box
            onClick={() => handleStudentStatusFilter('in_progress')}
            sx={{
              padding: 2,
              borderRadius: 2,
              backgroundColor:
                selectedStudentStatus === 'in_progress'
                  ? theme.palette.action.selected
                  : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold', mb: 0.5, color: 'warning.main' }}
            >
              {statsLoading ? '...' : studentsStats.inProgress}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              {t('courses:courseDetail.inProgress', 'In Progress')}
            </Typography>
          </Box>
          <Box
            onClick={() => handleStudentStatusFilter('started')}
            sx={{
              padding: 2,
              borderRadius: 2,
              backgroundColor:
                selectedStudentStatus === 'started'
                  ? theme.palette.action.selected
                  : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: 'bold', mb: 0.5, color: 'success.main' }}
            >
              {statsLoading ? '...' : studentsStats.started}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.secondary }}
            >
              {t('courses:courseDetail.started', 'Started')}
            </Typography>
          </Box>
        </Box>

        {/* Student search */}
        <Box sx={{ mb: 3 }}>
          <SearchInput
            value={studentSearchTerm}
            onChange={handleStudentSearch}
            placeholder={t(
              'courses:courseDetail.searchByNameOrEmail',
              'Search by name or email...'
            )}
            debounceTime={300}
            size="small"
          />
        </Box>

        {/* Student list */}
        <Box
          sx={{
            maxHeight: '320px',
            overflowY: 'auto',
            pr: 1,
          }}
        >
          {studentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : studentsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {studentsError}
            </Alert>
          ) : students.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography
                variant="body2"
                sx={{ color: theme.palette.text.secondary }}
              >
                {t('courses:courseDetail.noStudentsFound', 'No students found')}
              </Typography>
            </Box>
          ) : (
            <>
              {students.map((student) => {
                const studentName =
                  student.name ||
                  student.username ||
                  t('courses:courseDetail.unknownStudent', 'Unknown Student');

                return (
                  <Box
                    key={student._id}
                    component={Link}
                    to={`/profile/${student._id}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1.5,
                      borderRadius: 1.5,
                      mb: 0.5,
                      color: 'text.primary',
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <Avatar
                        src={getUserAvatarSrc(student)}
                        alt={studentName}
                        sx={{
                          width: 36,
                          height: 36,
                          backgroundColor: getChipBackgroundColor(
                            student.status
                          ),
                          color: getAvatarColor(student.status),
                          fontSize: '0.813rem',
                          fontWeight: 'bold',
                        }}
                      >
                        {studentName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, lineHeight: 1.3 }}
                        >
                          {studentName}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem',
                            lineHeight: 1.2,
                          }}
                        >
                          {student.email || 'No email'}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={t(
                        `courses:courseDetail.${student.status}`,
                        student.status
                      )}
                      size="small"
                      sx={{
                        fontSize: '0.688rem',
                        fontWeight: 600,
                        height: 22,
                        backgroundColor: getChipBackgroundColor(student.status),
                        color: getAvatarColor(student.status),
                        textTransform: 'capitalize',
                        '& .MuiChip-label': {
                          px: 1.5,
                          py: 0.25,
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </>
          )}
        </Box>

        {/* Pagination */}
        {!studentsLoading &&
          students.length > 0 &&
          studentsPagination.totalPages > 1 && (
            <Pagination
              count={studentsPagination.totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
              sx={{
                display: 'flex',
                justifyContent: 'center',
                '& .MuiPagination-ul': {
                  justifyContent: 'center',
                },
              }}
            />
          )}
      </DialogContent>
    </Dialog>
  );
};

export default StudentModal;
